import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef } from 'react';
import { useBoolean } from 'react-hanger';
import { getter, SortDescriptor } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { GridEvent, GridFilterChangeEvent, GridSortChangeEvent, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { saveAs } from 'file-saver';
import debounce from 'lodash/debounce';
import { concatMap, Subject } from 'rxjs';
import {
    API_GRID_TAKE,
    formFilters,
    formGridColumns,
    scrollGridToTop,
    SortDir,
} from '@components-clean/core/api-grid/api-grid.helper';
import { ApiGridProps, RefetchGridDataOptions } from '@components-clean/core/api-grid/api-grid.interface';
import { getGridHelper } from '@components-clean/core/api-grid/helpers/helper-provider';
import CoreGrid, { CoreGridHandle } from '@components-clean/core/grid';
import CardGrid from '@components-clean/core/grid/card-grid';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import Loader, { LoaderPosition } from '@components-clean/core/grid/grid-loader';
import {
    GridColumnProps,
    REORDER_COLUMN_ID,
    ROW_MENU_ACTION_COLUMN_ID,
} from '@components-clean/core/grid/grid.interface';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import {
    AdvanceFilter,
    ApiGridParams,
    ExportColumn,
    GridSort,
    InfiniteScrollGridResponse,
} from '@interfaces/central-monitoring';
import { useExportGridData, useGetGridConfig, useGetGridData, useGetGridIndex } from '@apis/bff-api/central-monitoring';

interface ProcessedColumn {
    orderIndex: number;
    position: number;
    show: boolean | undefined;
    key: string | undefined;
    children?: ProcessedColumn[];
}

interface ITableInfo {
    columns: GridColumnProps[];
}
export type ApiGridHandle = {
    addRowAndSelect: (primaryKey: string) => void;
    refreshRow: (primaryKey: string) => void;
    removeRow: (primaryKey: string, clearFilters?: boolean) => void;
    refreshData: (clearFilters?: boolean, resetSelectionState?: boolean) => void;
    getTotalRows: () => number;
    refetchConfig: () => void;
    unselectRow: (primaryKey: string) => void;
};

const httpRequestQueue$ = new Subject<{
    controllerKey: string;
    func: (signal: AbortSignal) => Promise<unknown>;
}>();

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const ApiGrid = forwardRef<ApiGridHandle, ApiGridProps>(
    (
        {
            maxHeight,
            testDataId,
            title,
            titleGroupByField,
            table,
            selectedTitle,
            onTitleChange,
            gridParams,
            rowMenuActions,
            filterable,
            titleReadOnly,
            noTitleMessage,
            fetchData = true,
            onChipHeaderSelected,
            selectedChipHeader,
            addCallback,
            canEdit,
            noPadding,
            screenFilterable,
            exportFilename,
            onSelectionChange,
            selectable,
            selectedField,
            dataItemKey,
            showAddIcon,
            showBulkUpdateIcon,
            showSendEmailIcon,
            gridStatusSetup,
            emailCallback,
            bulkUpdateTooltip,
            addRecordTooltip,
            filterListener,
            loading: parentLoading,
            onBulkUpdateClicked,
            isChildGrid,
            useFilterWarning,
            scrollable,
            showPlaceholder,
            useCardWrapper = true,
            apiGridMenuActionItems,
            titleWidth,
            rowHeight,
            gridDownloadDisabled = false,
            onTotalRowsCountChanged,
            integrationsOptions,
            gridRefresh,
        },
        ref,
    ) => {
        const { sessionId, filtersChanged$, isRecalculating, isFiltered } = useStudyDataFilterContext();
        const { trigger: getGridData, data: gridData, isMutating: dataLoading } = useGetGridData(gridParams);
        const {
            data: gridConfig,
            isLoading: configLoading,
            mutate: refetchGridConfig,
            isValidating: validatingConfig,
        } = useGetGridConfig(gridParams);
        const { trigger: gridIndex, isMutating: indexLoading } = useGetGridIndex(gridParams);
        const { trigger: exportGrid, isMutating: isExporting } = useExportGridData(gridParams);
        const [gridItems, setGridItems] = useState<InfiniteScrollGridResponse['data']>([]);
        const [filter, setFilter] = useState<CompositeFilterDescriptor>();
        const [skip, setSkip] = useState<number>(0);
        const [batching, setBatching] = useState<boolean>(false);
        const [loadingData, setLoadingData] = useState<boolean>(false);
        const [sort, setSort] = useState<SortDescriptor[]>();
        const [exportColumns, setExportColumns] = useState<GridColumnProps[]>([]);
        const [defaultSelectedItem, setDefaultSelectedItem] = useState<Record<string, never> | undefined>(undefined);
        const clearSelectedRowsEvent = useBoolean(false);
        const [gridKey, setGridKey] = useState(0);
        const [page, setPage] = useState({
            skip: 0,
            take: API_GRID_TAKE,
        });
        const abortControllersRef = useRef<Record<string, AbortController>>({});
        // Controller for the current refetchGridData request, so a newer call can abort it.
        const mainFetchAbortControllerRef = useRef<AbortController | null>(null);
        const coreGridRef = useRef<CoreGridHandle | null>(null);
        const customCells = createGridCustomCells();
        const { comparisonDatasetId } = gridParams as Extract<ApiGridParams, { grid: 'DataTable' }>;

        const tableInfo = useMemo<ITableInfo | undefined>(() => {
            if (!gridConfig || configLoading || validatingConfig) return undefined;
            return {
                columns: formGridColumns(
                    gridConfig,
                    getGridHelper(gridParams, customCells),
                    gridParams.grid === 'DataTable',
                ).flatMap((col, index) => {
                    return [{ ...col, orderIndex: index }];
                }),
            };
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [gridConfig, configLoading, validatingConfig, gridParams.grid, comparisonDatasetId]);

        const updateFilters = (newFilter?: CompositeFilterDescriptor) => {
            setFilter(newFilter);
            filterListener && filterListener(newFilter);
        };

        useEffect(() => {
            if (onTotalRowsCountChanged) {
                onTotalRowsCountChanged(gridData?.totalCount ?? 0);
            }
        }, [gridData?.totalCount, onTotalRowsCountChanged]);

        // Cancels any in-flight refetchGridData call before starting a new one, so a slow
        // stale response (e.g. from a pill switched away from) can't overwrite fresher data.
        const startMainFetch = (): AbortSignal => {
            // Abort the current request if there is one
            mainFetchAbortControllerRef.current?.abort('Superseded by newer request');
            const controller = new AbortController();
            // Set a new abort controller
            mainFetchAbortControllerRef.current = controller;
            return controller.signal;
        };

        const refetchGridData = ({ filters, newSort, reset, validateSelection }: RefetchGridDataOptions = {}) => {
            if (!fetchData) return;

            let sortData: { sort: GridSort } | Record<string, never> = {};
            let filtersData: { filters: AdvanceFilter[] } | Record<string, never> = { filters: [] };
            if (!reset) {
                if (newSort && !newSort.length) {
                    sortData = {};
                } else if (newSort?.length && newSort[0].dir) {
                    sortData = {
                        sort: {
                            table,
                            field: newSort[0].field,
                            dir: SortDir[newSort[0].dir],
                        },
                    };
                } else if (sort?.length && sort[0].dir) {
                    sortData = {
                        sort: {
                            table,
                            field: sort[0].field,
                            dir: SortDir[sort[0].dir],
                        },
                    };
                }

                if (filters) {
                    filtersData = { filters };
                } else if (filter) {
                    filtersData = { filters: formFilters(filter, table) };
                }
            }

            const signal = startMainFetch();

            getGridData({
                skip: 0,
                take: API_GRID_TAKE,
                filters: filtersData,
                sessionId: !!sessionId ? sessionId : null,
                signal,
                ...sortData,
            })
                .then((resp) => {
                    // A newer refetch has since superseded this one; its response should win instead.
                    if (signal.aborted) return;
                    if (resp.data) {
                        if (scrollable === 'virtual') {
                            const newData = new Array(resp.totalCount).fill({
                                isLoadingCell: true,
                            });
                            resp.data.forEach((item, index) => {
                                newData[index] = item;
                            });

                            setGridItems(newData);
                            // Not great, have to pass newData in as can't rely on the state in core grid having been updated yet
                            if (validateSelection) coreGridRef.current?.validateSelection(newData);
                        } else setGridItems(resp.data);
                    }
                })
                .catch((err) => window.console.error(err));
        };

        const fetchDataInBatches = (
            start: number,
            end: number,
            sortData: { sort: GridSort } | Record<string, never>,
        ) => {
            setBatching(true);
            const batchPromises = [];
            for (let i = start; i < end; i += API_GRID_TAKE) {
                batchPromises.push(
                    getGridData({
                        skip: i,
                        take: API_GRID_TAKE,
                        sessionId: !!sessionId ? sessionId : null,
                        ...sortData,
                    }).then((response) => ({ skip: i, data: response.data })),
                );
            }
            // Return the promise chain
            return Promise.all(batchPromises).then((batchResults) => {
                batchResults.sort((a, b) => a.skip - b.skip); // Ensure order is maintained
                return batchResults.flatMap((batch) => batch.data);
            });
        };

        const idGetter = getter(dataItemKey ?? '');

        const removeRow = (primaryKey: string, clearFilters: boolean) => {
            const filterApplied: boolean = !!filter;

            if (scrollable === 'virtual' && gridItems) {
                if (clearFilters) {
                    updateFilters(undefined);
                    setGridKey((prev) => prev + 1);
                }
                if (filterApplied) {
                    refetchGridData({ reset: false, filters: clearFilters ? [] : formFilters(filter, table) });
                } else {
                    const index = gridItems.findIndex((item) => idGetter(item) === primaryKey) ?? -1;
                    if (index > -1) {
                        gridItems.splice(index, 1);
                        setGridItems(gridItems);
                        if (gridData?.totalCount != null) gridData.totalCount -= 1;
                    }
                }
                coreGridRef.current?.unselectRow(primaryKey);
                return;
            }

            if (gridData && gridData.totalCount! >= API_GRID_TAKE && !filterApplied) {
                const updatedItems = gridItems?.filter((item) => idGetter(item) !== primaryKey.toString());
                const index = updatedItems?.length ?? 0;

                let sortData: { sort: GridSort } | Record<string, never>;

                if (sort?.length && sort[0].dir) {
                    sortData = {
                        sort: {
                            table,
                            field: sort[0].field,
                            dir: SortDir[sort[0].dir],
                        },
                    };
                } else {
                    sortData = {};
                }

                getGridData({
                    skip: index,
                    take: 1,
                    sessionId: !!sessionId ? sessionId : null,
                    ...sortData,
                })
                    .then((resp) => {
                        if (!resp.data) return;
                        const newItem = resp.data[0];
                        if (updatedItems && gridItems && updatedItems.length > 0) {
                            // we add the item to the end of the list
                            updatedItems.splice(index, 0, newItem);
                            setGridItems(updatedItems);
                        } else {
                            const updatedData = [];
                            updatedData.push(newItem);
                            setGridItems(updatedData);
                        }
                    })
                    .catch((err) => window.console.error(err));
            } else {
                updateFilters(undefined);
                setGridKey((prev) => prev + 1);
                const f: AdvanceFilter[] = [];
                refetchGridData({ reset: false, filters: f });
            }
        };

        const createAbortController = () => {
            const controller = new AbortController();
            const key = window.crypto.randomUUID().slice(0, 8);
            abortControllersRef.current[key] = controller;

            return key;
        };

        const selectRow = (primaryKey: string) => {
            const filterApplied: boolean = !!filter;
            updateFilters(undefined);
            setGridKey((prev) => prev + 1);
            // check if record is in the grid, if so this is an update, and we first need to remove the item from the grid.
            const inGrid = gridItems?.find((item) => idGetter(item) === primaryKey.toString());
            let updatedItems = gridItems;
            if (inGrid) {
                // Remove the item from gridItems
                updatedItems = gridItems?.filter((item) => idGetter(item) !== primaryKey.toString());
            }
            let sortData: { sort: GridSort } | Record<string, never> = {};

            if (sort?.length && sort[0].dir) {
                sortData = {
                    sort: {
                        table,
                        field: sort[0].field,
                        dir: SortDir[sort[0].dir],
                    },
                };
            } else {
                sortData = {};
            }
            setLoadingData(true);
            // The primaryKey is used to find the index of the item in the grid
            gridIndex({
                skip: 0,
                take: API_GRID_TAKE,
                sessionId: !!sessionId ? sessionId : null,
                indexId: primaryKey as unknown as number,
                ...sortData,
            })
                .then((response) => {
                    if (response) {
                        const index = response;
                        if (scrollable === 'virtual') {
                            const key = createAbortController();
                            // we find the added/updated item and splice it in to the grid
                            httpRequestQueue$.next({
                                controllerKey: key,
                                func: (signal: AbortSignal) => {
                                    const s = Math.max(0, index - 1 - API_GRID_TAKE);
                                    return getGridData({
                                        skip: s,
                                        take: API_GRID_TAKE * 2,
                                        sessionId: !!sessionId ? sessionId : null,
                                        signal,
                                        ...sortData,
                                    })
                                        .then((resp) => {
                                            if (signal.aborted || !resp.data) return;
                                            const newData = new Array(resp.totalCount).fill({
                                                isLoadingCell: true,
                                            });
                                            resp.data.forEach((item, i) => {
                                                newData[s + i] = item;
                                            });
                                            const newItem = newData[index - 1] as Record<string, never>;

                                            setGridItems(newData);
                                            setDefaultSelectedItem(newItem);
                                        })
                                        .finally(() => {
                                            setLoadingData(false);
                                        })
                                        .catch((err) => window.console.log(err));
                                },
                            });
                            return;
                        }

                        // if we have applied filters, we need to get the all the data in batches until we reach the index
                        if (filterApplied) {
                            const start = 0;
                            const end = Math.ceil(index / API_GRID_TAKE) * API_GRID_TAKE;
                            fetchDataInBatches(start, end, sortData)
                                .then((newData) => {
                                    setGridItems(newData);
                                    setSkip(end);
                                    const selectedItem = newData[index - 1] as Record<string, never>;
                                    setDefaultSelectedItem(selectedItem);
                                })
                                .catch((err) => window.console.error(err))
                                .finally(() => setLoadingData(false));
                        } else if (
                            updatedItems &&
                            updatedItems.length > 0 &&
                            index > updatedItems.length &&
                            index > API_GRID_TAKE
                        ) {
                            // if we have no filters, and we have scrolled down, we need to get the data in batches until we reach the index
                            const start = Math.max(0, updatedItems.length);
                            const end = Math.ceil(index / API_GRID_TAKE) * API_GRID_TAKE;
                            fetchDataInBatches(start, end, sortData)
                                .then((newData) => {
                                    const newGridItems = updatedItems ? updatedItems.concat(newData) : newData;
                                    setGridItems(newGridItems);
                                    setSkip(end);
                                    const selectedItem = newGridItems[index - 1] as Record<string, never>;
                                    setDefaultSelectedItem(selectedItem);
                                })
                                .catch((err) => window.console.error(err))
                                .finally(() => setLoadingData(false));
                        } else {
                            // we find the added/updated item and splice it in to the grid
                            getGridData({
                                skip: index - 1,
                                take: 1,
                                sessionId: !!sessionId ? sessionId : null,
                                ...sortData,
                            })
                                .then((resp) => {
                                    if (!resp.data) return;
                                    const newItem = resp.data[0];
                                    if (updatedItems && gridItems && updatedItems.length > 0) {
                                        updatedItems.splice(index - 1, 0, newItem);
                                        if (
                                            updatedItems.length > gridItems.length &&
                                            updatedItems.length >= API_GRID_TAKE
                                        ) {
                                            updatedItems.pop();
                                        }
                                        setGridItems(updatedItems);
                                    } else {
                                        const updatedData = [];
                                        updatedData.push(newItem);
                                        setGridItems(updatedData);
                                    }
                                    setDefaultSelectedItem(newItem as Record<string, never>);
                                })
                                .catch((err) => window.console.error(err))
                                .finally(() => setLoadingData(false));
                        }
                    } else {
                        refetchGridData({ reset: false });
                    }
                })
                .catch((err) => {
                    window.console.error(err);
                    setLoadingData(false);
                })
                .finally(() => {
                    setBatching(false);
                    setLoadingData(false);
                });
        };

        // This only works for virtualized grid. All of our API grids should soon be virtualized
        const refreshRowAndSelect = (primaryKey: string) => {
            if (scrollable !== 'virtual') return;
            const existingIndex = gridItems?.findIndex((item) => idGetter(item) === primaryKey) ?? -1;
            if (existingIndex < 0) return; // The row doesn't exist in the grid, nothing to refresh

            let sortData: { sort: GridSort } | Record<string, never> = {};
            let filtersData: { filters: AdvanceFilter[] } | Record<string, never> = { filters: [] };
            if (sort?.length && sort[0].dir) {
                sortData = {
                    sort: {
                        table,
                        field: sort[0].field,
                        dir: SortDir[sort[0].dir],
                    },
                };
            }

            if (filter) {
                filtersData = { filters: formFilters(filter, table) };
            }

            gridIndex({
                skip: 0,
                take: 1,
                sessionId: !!sessionId ? sessionId : null,
                indexId: primaryKey as unknown as number,
                sort: sortData.sort,
                filters: filtersData,
            })
                .then((index) => {
                    // This will be starting at 1, not 0
                    if (!index) {
                        // It's no longer part of the results (maybe due to an applied filter)
                        setGridItems((old) => {
                            if (!old) return old;
                            const newData = [...old];
                            newData.splice(existingIndex, 1);
                            return newData;
                        });

                        coreGridRef.current?.unselectRow(primaryKey);
                        return;
                    }

                    // Load the data for the row.
                    getGridData({
                        skip: index - 1,
                        take: 1,
                        sessionId: !!sessionId ? sessionId : null,
                        indexId: primaryKey as unknown as number,
                        sort: sortData.sort,
                        filters: filtersData,
                    })
                        .then((rowResp) => {
                            if (!rowResp.data) return;
                            const row = rowResp.data[0];
                            if (existingIndex === index - 1) {
                                // The row hasn't changed position, update it in place
                                setGridItems((old) => {
                                    if (!old) return old;
                                    const newData = [...old];
                                    newData[index - 1] = row;

                                    return newData;
                                });
                            } else {
                                // Row has change position. Remove it from it's current position, insert new row at new index
                                setGridItems((old) => {
                                    if (!old) return old;
                                    const newData = [...old];
                                    newData.splice(existingIndex, 1);
                                    newData.splice(index - 1, 0, row);
                                    return newData;
                                });
                                setDefaultSelectedItem(row as Record<string, never>);
                            }
                        })
                        .catch((err) => window.console.error(err));
                })
                .catch((err) => window.console.error(err));
        };

        const abortRequests = () => {
            Object.keys(abortControllersRef.current).forEach((key) =>
                abortControllersRef.current[key].abort('Request aborted'),
            );
            // This is separate to the abortControllersRef dictionary, so abort separately
            mainFetchAbortControllerRef.current?.abort('Request aborted');
        };

        const resetToFirstPage = () => {
            abortRequests();
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setPage({
                skip: 0,
                take: API_GRID_TAKE,
            });
        };

        useImperativeHandle(ref, () => ({
            addRowAndSelect(primaryKey: string) {
                selectRow(primaryKey);
            },
            refreshRow(primaryKey: string) {
                refreshRowAndSelect(primaryKey);
            },
            removeRow(primaryKey: string, clearFilters: boolean = true) {
                removeRow(primaryKey, clearFilters);
            },
            refreshData(clearFilters: boolean = true, resetSelectionState?: boolean) {
                setSkip(0);
                resetToFirstPage();
                let f: AdvanceFilter[] = [];
                if (clearFilters) {
                    updateFilters(undefined);
                    setGridKey((prev) => prev + 1);
                } else f = formFilters(filter, table);
                refetchGridData({ reset: false, filters: f, validateSelection: !!f.length });

                if (resetSelectionState) coreGridRef.current?.clearSelectedRows();
            },
            getTotalRows() {
                return gridData?.totalCount ?? 0;
            },
            refetchConfig() {
                refetchGridConfig().catch((err) => window.console.error(err));
            },
            unselectRow(primaryKey: string) {
                coreGridRef.current?.unselectRow(primaryKey);
            },
        }));

        // Loads data for virtual scroll
        const requestData = (skipParam: number) => {
            const filters = formFilters(filter, table);
            const sortData =
                sort?.length && sort[0].dir
                    ? {
                          table,
                          field: sort[0].field,
                          dir: SortDir[sort[0].dir],
                      }
                    : undefined;

            const key = createAbortController();

            httpRequestQueue$.next({
                controllerKey: key,
                func: (signal: AbortSignal) => {
                    const halfPageSize = API_GRID_TAKE / 2;
                    const s = Math.max(0, skipParam - halfPageSize);

                    return getGridData(
                        {
                            skip: s,
                            take: API_GRID_TAKE * 2,
                            filters: { filters: filters ?? [] },
                            sessionId: !!sessionId ? sessionId : null,
                            sort: sortData,
                            signal,
                        },
                        {},
                    )
                        .then((resp) => {
                            if (signal.aborted) return;
                            if (resp.data?.length) {
                                let newData = [];
                                if (!gridItems || gridItems.length === 0)
                                    newData = new Array(resp.totalCount).fill({
                                        isLoadingCell: true,
                                    });
                                else newData = [...gridItems];

                                resp.data.forEach((item, index) => {
                                    newData[index + s] = item;
                                });
                                setGridItems(newData);
                            }
                        })
                        .catch((err) => {
                            window.console.error(err);
                        });
                },
            });
        };

        const requestIfNeeded = (skipParam: number) => {
            if (!gridItems) requestData(0);
            else
                for (let i = skipParam; i < skipParam + API_GRID_TAKE; i++) {
                    // Remove dependency on dataItemKey as not all Data Tables have one. The only risk here
                    // is that the grid could request data if a legitimate row has a property called isLoadingCell.
                    // However, this would previously be a problem if the dataItemKey was 'isLoadignCelli (unlikely/impossible)
                    if (typeof gridItems[i] === 'object' && Object.hasOwn(gridItems[i] as object, 'isLoadingCell')) {
                        // request data only if not already fetched
                        requestData(i);
                        return;
                    }
                }
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
        const debouncePageChange = useCallback(
            debounce((e: GridPageChangeEvent) => {
                requestIfNeeded(e.page.skip);
            }, 200),
            [gridItems],
        );

        const scrollHandler = (event: GridEvent) => {
            const e = event.nativeEvent as Event;
            const target = e.target as HTMLMapElement;
            const scrollAvailable = target.scrollHeight - target.clientHeight;
            // NOTE: Should get more data when we scrolled 3/4 of the available scroll
            const scrollToGetMoreData = scrollAvailable * 0.25;
            const filters = formFilters(filter, table);
            const sortData =
                sort?.length && sort[0].dir
                    ? {
                          table,
                          field: sort[0].field,
                          dir: SortDir[sort[0].dir],
                      }
                    : undefined;

            if (
                target.scrollTop + 10 >= scrollAvailable - scrollToGetMoreData &&
                !dataLoading &&
                gridData &&
                gridItems &&
                gridData.totalCount !== gridItems.length
            ) {
                getGridData({
                    skip: skip + API_GRID_TAKE,
                    take: API_GRID_TAKE,
                    filters: { filters: filters ?? [] },
                    sessionId: !!sessionId ? sessionId : null,
                    sort: sortData,
                })
                    .then((resp) => {
                        if (resp.data?.length) {
                            setGridItems((oldData) => (oldData ?? []).concat(resp.data));
                        }
                    })
                    .catch((err) => window.console.error(err));
                setSkip(skip + API_GRID_TAKE);
            }
        };

        const exportExcelFile = () => {
            const sortData: GridSort | undefined =
                sort?.length && sort[0].dir
                    ? {
                          table,
                          field: sort[0].field,
                          dir: SortDir[sort[0].dir],
                      }
                    : undefined;
            const filtersData: { filters: AdvanceFilter[] } | Record<string, never> = {
                filters: formFilters(filter, table),
            };

            const processColumns = (
                cols: GridColumnProps[] | undefined,
                useColumnPosition = false,
            ): ProcessedColumn[] => {
                if (!cols?.length) return [];

                return cols
                    .filter((col) => col.id !== REORDER_COLUMN_ID && col.id !== ROW_MENU_ACTION_COLUMN_ID)
                    .map((item, index) => ({
                        orderIndex: item?.orderIndex as number,
                        position: useColumnPosition ? (item.columnPosition as number) : index,
                        show: item.show,
                        key: item.field,
                        children: processColumns(item.children, false),
                    }))
                    .filter((col) => col.show)
                    .sort((a, b) => a.orderIndex - b.orderIndex);
            };

            const useColumnPosition = gridParams.grid === 'DataTable' || gridParams.grid === 'SMDataTable';
            const columns = processColumns(exportColumns, useColumnPosition);

            const formExportColumns = (columnsToMap: ProcessedColumn[]) => {
                return columnsToMap
                    .filter((column) => column.show)
                    .map((column, index): ExportColumn => ({
                        key: index,
                        fieldName: column.key as string,
                        children: column.children ? formExportColumns(column.children) : undefined,
                    }));
            };

            exportGrid({
                filters: filtersData.filters.length > 0 ? filtersData : null,
                sort: sortData,
                columns: columns.map((column) => column.position),
                sessionId: !!sessionId ? sessionId : undefined,
                advancedColumns: gridParams.grid !== 'DataTable' ? formExportColumns(columns) : undefined,
            })
                .then((result) => {
                    if (result.blob == null) throw new Error();
                    let fileName = result.fileName || 'Data.xlsx';
                    // Make sure the exportFilename does not contain a . because it will confuse Windows
                    if (exportFilename) fileName = `${exportFilename?.replaceAll('.', '')}.xlsx`;

                    saveAs(result.blob, fileName);
                })
                .catch((err) => {
                    window.console.error(err);
                });
        };

        const onSortChange = ({ sort: newSort }: GridSortChangeEvent) => {
            resetToFirstPage();
            setSort(newSort);
            refetchGridData({ newSort });
            setSkip(0);
        };

        const onFilterChange = (event: GridFilterChangeEvent) => {
            resetToFirstPage();
            if (useFilterWarning && event?.filter) {
                clearSelectedRowsEvent.setTrue();
                coreGridRef.current?.clearSelectedRows();
            }
            refetchGridData({ filters: formFilters(event.filter, table) });
            updateFilters(event.filter);
        };

        const clearFilters = (reset?: boolean) => {
            resetToFirstPage();
            updateFilters(undefined);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setGridKey((prev) => prev + 1);
            refetchGridData({ filters: [], reset });
        };

        useEffect(() => {
            const subscription = filtersChanged$.subscribe(() => {
                if (screenFilterable) {
                    scrollGridToTop(testDataId);
                    setSkip(0);
                    setGridItems([]);
                    clearFilters();
                }
            });
            return () => subscription.unsubscribe();
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [screenFilterable, sort, sessionId]);

        useEffect(() => {
            if (screenFilterable && !isRecalculating && isFiltered && !dataLoading) refetchGridData();
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [isRecalculating]);

        useEffect(() => {
            resetToFirstPage();
            scrollGridToTop(testDataId);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setSkip(0);
            debouncePageChange?.cancel();
            setSort([]);
            setGridItems([]);
            /* eslint-enable @eslint-react/set-state-in-effect */
            clearFilters(true);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [...Object.values(gridParams)]);

        useEffect(() => {
            const subscription = httpRequestQueue$
                .pipe(
                    concatMap((r) => {
                        const controller = abortControllersRef.current[r.controllerKey];
                        if (!controller || controller.signal.aborted) return Promise.resolve();

                        return r
                            .func(controller.signal)
                            .finally(() => delete abortControllersRef.current[r.controllerKey]);
                    }),
                )
                .subscribe();

            // Return a function that unsubscribes from the observable
            return () => {
                // Stop any API requests currently in-flight
                abortRequests();
                subscription.unsubscribe();
            };
        }, []);

        const loading = dataLoading || indexLoading || loadingData || batching || parentLoading;
        const gridTotalCount = gridData?.totalCount || 0;

        if (configLoading || !tableInfo) {
            return (
                <div className="k-pos-relative k-pb-7">
                    <Loader position={LoaderPosition.bottom} />
                </div>
            );
        }

        const gridComponentProps = {
            ref: coreGridRef,
            title,
            titleGroupByField,
            selectedTitle,
            onTitleChange,
            maxHeight,
            testDataId,
            data: gridItems,
            columns: tableInfo.columns,
            onScroll: scrollable !== 'virtual' ? scrollHandler : undefined,
            fixedScroll: scrollable !== 'virtual',
            totalCount: gridTotalCount,
            sortable: true,
            sort,
            onSortChange,
            filter,
            onFilterChange,
            clearFilter: clearFilters,
            isExporting,
            exportCallback: () => exportExcelFile(),
            setExportColumns: (newCols: GridColumnProps[]) => {
                setExportColumns(newCols);
            },
            rowMenuActions,
            filterable,
            titleReadOnly,
            noTitleMessage,
            onChipHeaderSelected,
            selectedChipHeader,
            addCallback,
            canEdit,
            noPadding,
            dataItemKey,
            selectable,
            onSelectionChange,
            selectedField,
            loading,
            screenFilterable,
            showAddIcon,
            showBulkUpdateIcon,
            showSendEmailIcon,
            gridStatusSetup,
            emailCallback,
            defaultSelectedItem,
            bulkUpdateTooltip,
            addRecordTooltip,
            onBulkUpdateClicked,
            isChildGrid,
            clearGridSelectedRows: clearSelectedRowsEvent.value,
            scrollable,
            rowHeight: scrollable === 'virtual' ? 30 : rowHeight,
            skip: scrollable === 'virtual' ? page.skip : undefined,
            total: scrollable === 'virtual' ? gridTotalCount : undefined,
            pageSize: scrollable === 'virtual' ? API_GRID_TAKE : undefined,
            onPageChange:
                scrollable === 'virtual'
                    ? (e: GridPageChangeEvent) => {
                          setPage(e.page);
                          debouncePageChange(e);
                      }
                    : undefined,
            apiGrid: true,
            showPlaceholder,
            gridKey,
            setGridKey,
            apiGridMenuActionItems,
            titleWidth,
            gridDownloadDisabled,
            integrationsOptions,
            gridRefresh,
        };

        return (
            <div className="k-pos-relative k-h-full">
                {useCardWrapper ? (
                    <CardGrid key={selectedTitle?.id} {...gridComponentProps} />
                ) : (
                    <CoreGrid key={selectedTitle?.id} {...gridComponentProps} />
                )}
            </div>
        );
    },
);

export default ApiGrid;
